import { useState, ChangeEvent } from "react";
import { Box, Typography, Alert } from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";
import { ActionButton, FormDialog, InputField } from "../v3";
import { ProjectService } from "../../services";
import { toast } from "react-toastify";

interface AddDocumentModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string | number;
  onSuccess: () => void;
  currentCount: number;
}

const DOCUMENT_LIMIT = 20;

export const AddDocumentModal = ({ open, onClose, projectId, onSuccess, currentCount }: AddDocumentModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState<string>("");

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const isLimitReached = currentCount >= DOCUMENT_LIMIT;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error("File size must be below 5MB");
        e.target.value = ""; // Clear the input
        return;
      }
      setFile(selectedFile);
      if (!documentName) {
        setDocumentName(selectedFile.name.split(".").slice(0, -1).join("."));
      }
    }
  };

  const handleUpload = async () => {
    if (isLimitReached) {
      toast.error(`Maximum document limit (${DOCUMENT_LIMIT}) reached.`);
      return;
    }
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    if (!documentName) {
      toast.error("Please enter a document name");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    // The backend expects the field name to be 'document'
    formData.append("document", file, documentName);

    try {
      await ProjectService.uploadDocument(projectId, formData);
      toast.success("Document uploaded successfully");
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return; // Prevent closing while uploading
    setFile(null);
    setDocumentName("");
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      onSubmit={handleUpload}
      title="Add Document"
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleClose} variant="outlined" disabled={loading} />
          <ActionButton buttonText="Upload" type="submit" loading={loading} disabled={isLimitReached} />
        </>
      }
    >
      <Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 3 }}>
        {isLimitReached && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Maximum document limit of {DOCUMENT_LIMIT} reached. Please delete existing documents to upload new ones.
          </Alert>
        )}

        <InputField
          label="Document Name"
          value={documentName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setDocumentName(e.target.value)}
          fullWidth
          required
          disabled={loading || isLimitReached}
        />

        <Box
          sx={{
            border: "2px dashed #e0e0e0",
            borderRadius: 2,
            p: 8,
            textAlign: "center",
            cursor: loading || isLimitReached ? "default" : "pointer",
            backgroundColor: "#fafafa",
            opacity: loading || isLimitReached ? 0.6 : 1,
            "&:hover": {
              backgroundColor: loading || isLimitReached ? "#fafafa" : "#f5f5f5",
              borderColor: loading || isLimitReached ? "#e0e0e0" : "#674BF5",
            },
          }}
          onClick={() => !loading && !isLimitReached && document.getElementById("file-upload")?.click()}
        >
          <input
            id="file-upload"
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            disabled={loading || isLimitReached}
          />
          <CloudUploadOutlined sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
          <Typography variant="body1" color="textSecondary">
            {file ? file.name : isLimitReached ? "Upload Disabled" : "Click to select document"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supported formats: PDF, Image (Max 5MB)
          </Typography>
        </Box>
      </Box>
    </FormDialog>
  );
};
